/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useState, type RefObject } from "react";

/**
 * Когда обрезать картинку нельзя.
 *
 * По умолчанию кадр растягивается на всю плитку с обрезкой (`cover`): так не
 * остаётся чёрных полей и лица крупнее. Пока камера и плитка близки по форме,
 * срезается кромка и это незаметно.
 *
 * Ломается это на камерах другой формы. CTO 11.09.2026: «меня было видно только
 * половину» — снимок встречи, снятый у коллеги на маке. По журналу звонилки
 * (комната !T6M9lLc…) его настольный клиент отдавал **640×480**, то есть 4:3,
 * тогда как остальные участники — 1280×720. Плитки в сетке всегда лежачие и
 * растягиваются до 17:9 (`arrangeTiles`), поэтому у широкого окна от кадра 4:3
 * обрезается почти треть высоты — макушка и грудь.
 *
 * Отсюда же ответ, почему он не видел этого у себя: та же картинка обрезается
 * по-разному в зависимости от ширины чужого окна. У узкого окна плитка ближе к
 * 4:3 и не режет ничего, у широкого — режет.
 *
 * Поэтому обрезаем, пока она съедает немного, и вписываем кадр целиком, когда
 * съела бы слишком много. Порог — доля исходного кадра, которая остаётся видна.
 */

/**
 * Сколько кадра должно остаться видно, чтобы обрезка считалась приемлемой.
 *
 * 0,85 выбран по настоящим числам той встречи: кадр 16:9 в самой широкой плитке
 * 17:9 теряет 6% и обрезается как прежде, а кадр 4:3 там же терял бы 29% — его
 * вписываем целиком. Полоски по краям лучше срезанной макушки.
 */
const MIN_VISIBLE = 0.85;

/**
 * Какая доля кадра останется видна, если растянуть его с обрезкой.
 * Единица — формы совпали, обрезать нечего.
 */
export function visibleUnderCover(
  videoWidth: number,
  videoHeight: number,
  tileWidth: number,
  tileHeight: number,
): number {
  if (videoWidth <= 0 || videoHeight <= 0 || tileWidth <= 0 || tileHeight <= 0)
    return 1;
  const video = videoWidth / videoHeight;
  const tile = tileWidth / tileHeight;
  return video > tile ? tile / video : video / tile;
}

/** Обрезать ли кадр при таких размерах. */
export function shouldCrop(
  videoWidth: number,
  videoHeight: number,
  tileWidth: number,
  tileHeight: number,
): boolean {
  return (
    visibleUnderCover(videoWidth, videoHeight, tileWidth, tileHeight) >=
    MIN_VISIBLE
  );
}

/**
 * Можно ли обрезать кадр в этой плитке. `true`, пока размеры неизвестны:
 * прежнее поведение остаётся исходным, а вписываем только когда убедились.
 */
export function useSafeVideoFit(ref: RefObject<HTMLElement | null>): boolean {
  const [crop, setCrop] = useState(true);

  const measure = useCallback((): void => {
    const root = ref.current;
    const video = root?.querySelector("video");
    if (!root || !video) return;
    const rect = root.getBoundingClientRect();
    setCrop(
      shouldCrop(video.videoWidth, video.videoHeight, rect.width, rect.height),
    );
  }, [ref]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    measure();

    // Размеры приходят не сразу: сперва появляется элемент, потом первый кадр,
    // а плитка меняется при перестроении сетки и при повороте телефона.
    // ResizeObserver есть не везде (проверки идут без него) — без него просто
    // не пересчитываем на изменение плитки.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(root);

    const video = root.querySelector("video");
    video?.addEventListener("loadedmetadata", measure);
    video?.addEventListener("resize", measure);

    return () => {
      observer?.disconnect();
      video?.removeEventListener("loadedmetadata", measure);
      video?.removeEventListener("resize", measure);
    };
  }, [ref, measure]);

  return crop;
}
